import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import {
  INSTITUTIONS_ENABLED,
  INSTITUTIONS_PUBLIC,
  institutionsAdminPreview,
} from '../../lib/featureFlags';
import { getStudentMembershipForSlug } from '../../lib/institutionOnboarding';
import { FullPageLoader } from '../ui/page-loader';

const Loading = () => <FullPageLoader message="Loading" />;

const ModuleDisabled = () => <Navigate to="/" replace />;

const AdminPreviewDenied = () => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="max-w-md text-center space-y-4">
      <h1 className="text-xl font-cabinet font-bold">Institutions preview</h1>
      <p className="text-muted-foreground text-sm">
        This area is in development. Log in with a platform admin account to access it.
      </p>
      <a href="/login" className="text-primary underline text-sm">Log in</a>
    </div>
  </div>
);

/** Blocks all institution routes when the module is off. */
export const InstitutionModuleRoute = ({ children }) => {
  if (!INSTITUTIONS_ENABLED) return <ModuleDisabled />;
  return children;
};

/**
 * Public institution marketing pages (landing, login, trial).
 * When not public, only platform admins may access (after logging in).
 */
export const InstitutionMarketingRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { isInstitutionStaff, loading: instLoading } = useInstitution();
  const location = useLocation();

  if (authLoading || instLoading) return <Loading />;

  if (INSTITUTIONS_PUBLIC) {
    if (isAuthenticated && isInstitutionStaff && location.pathname !== '/institutions/trial') {
      return <Navigate to="/institutions/dashboard" replace />;
    }
    return children;
  }

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (!isAdmin) return <Navigate to="/" replace />;

  if (isInstitutionStaff && location.pathname !== '/institutions/trial') {
    return <Navigate to="/institutions/dashboard" replace />;
  }

  return children;
};

/** Staff dashboard and management pages. */
export const InstitutionProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { isInstitutionStaff, loading: instLoading } = useInstitution();

  if (authLoading || instLoading) return <Loading />;

  if (!isAuthenticated) {
    return <Navigate to="/institutions/login" replace />;
  }

  const adminPreview = institutionsAdminPreview(isAdmin);
  if (!isInstitutionStaff && !adminPreview) {
    if (!INSTITUTIONS_PUBLIC) {
      return <AdminPreviewDenied />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-cabinet font-bold">No institution access</h1>
          <p className="text-muted-foreground text-sm">
            You need an institution staff account to access this area.
            Start a trial or ask your administrator for access.
          </p>
          <a href="/institutions/trial" className="text-primary underline text-sm">Start institution trial</a>
        </div>
      </div>
    );
  }

  return children;
};

/** Student classroom portal at /i/:slug — always render; page handles auth/enrollment states. */
export const InstitutionStudentPortalRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const { studentMemberships, loading: instLoading } = useInstitution();
  const location = useLocation();
  const { slug } = useParams();

  if (authLoading || (isAuthenticated && instLoading)) return <Loading />;

  if (INSTITUTIONS_PUBLIC) return children;

  if (institutionsAdminPreview(isAdmin)) return children;

  if (!isAuthenticated) return children;

  if (slug && getStudentMembershipForSlug(studentMemberships, slug)) return children;

  return children;
};

/** Invite acceptance — always reachable when module is enabled (direct link only). */
export const InstitutionInviteRoute = ({ children }) => {
  if (!INSTITUTIONS_ENABLED) return <ModuleDisabled />;
  return children;
};

/** @deprecated use InstitutionMarketingRoute */
export const InstitutionPublicRoute = InstitutionMarketingRoute;
